import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const Noticias = () => {
    const history = useHistory();
    const [noticias, setNoticias] = useState([]);
    const [filteredNoticias, setFilteredNoticias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [noticiaToDelete, setNoticiaToDelete] = useState(null);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const itemsPerPage = 20;

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchNoticias();
    }, [history]);

    useEffect(() => {
        const filtered = noticias.filter(noticia =>
            noticia.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            noticia.contenido.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredNoticias(filtered);
        setCurrentPage(1);
    }, [searchTerm, noticias]);

    const fetchNoticias = async () => {
        try {
            const response = await axios.get('/superadmin/noticias');
            setNoticias(response.data);
            setFilteredNoticias(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching noticias:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const handleToggleEstado = async (id, estadoActual) => {
        try {
            await axios.put(`/superadmin/noticias/${id}/toggle-estado`);
            setAlert({
                show: true,
                type: 'success',
                message: `Noticia ${estadoActual ? 'desactivada' : 'activada'} exitosamente`
            });
            fetchNoticias();
        } catch (error) {
            console.error('Error toggling estado:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: 'Error al cambiar el estado de la noticia'
            });
        } finally {
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const handleDeleteClick = (noticia) => {
        setNoticiaToDelete(noticia);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(`/superadmin/noticias/${noticiaToDelete.id}`);
            setAlert({
                show: true,
                type: 'success',
                message: 'Noticia eliminada exitosamente'
            });
            fetchNoticias();
        } catch (error) {
            console.error('Error deleting noticia:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: 'Error al eliminar la noticia'
            });
        } finally {
            setShowDeleteModal(false);
            setNoticiaToDelete(null);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const getTipoLabel = (tipo) => {
        const tipos = {
            'info': 'Información',
            'secondary': 'Neutral',
            'light': 'Claro',
            'dark': 'Oscuro',
            'warning': 'Advertencia',
            'danger': 'Problema',
            'success': 'Éxito'
        };
        return tipos[tipo] || tipo;
    };

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            'info': 'badge-info',
            'secondary': 'badge-secondary',
            'light': 'badge-light',
            'dark': 'badge-dark',
            'warning': 'badge-warning',
            'danger': 'badge-danger',
            'success': 'badge-success'
        };
        return classes[tipo] || 'badge-secondary';
    };

    const getPrioridadLabel = (prioridad) => {
        const prioridades = {
            'baja': 'Baja',
            'media': 'Media',
            'alta': 'Alta',
            'critica': 'Crítica'
        };
        return prioridades[prioridad] || prioridad;
    };

    const getPrioridadBadgeClass = (prioridad) => {
        const classes = {
            'baja': 'badge-secondary',
            'media': 'badge-info',
            'alta': 'badge-warning',
            'critica': 'badge-danger'
        };
        return classes[prioridad] || 'badge-secondary';
    };

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredNoticias.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredNoticias.length / itemsPerPage);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4><i className="fas fa-bullhorn"></i> Noticias del Sistema</h4>
                <Link to="/superadmin/noticias/nueva" className="btn btn-superadmin">
                    <i className="fas fa-plus"></i> Nueva Noticia
                </Link>
            </div>

            {/* Alertas */}
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            {/* Búsqueda */}
            <div className="form-group">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar noticia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Tabla */}
            <div className="table-responsive">
                <table className="table table-hover superadmin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Título</th>
                            <th>Tipo</th>
                            <th>Prioridad</th>
                            <th>Fecha Inicio</th>
                            <th>Fecha Fin</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map(noticia => (
                                <tr key={noticia.id}>
                                    <td>{noticia.id}</td>
                                    <td>
                                        <strong>{noticia.titulo}</strong>
                                        <br />
                                        <small className="text-muted">
                                            {noticia.contenido.substring(0, 60)}...
                                        </small>
                                    </td>
                                    <td>
                                        <span className={`badge ${getTipoBadgeClass(noticia.tipo)}`}>
                                            {getTipoLabel(noticia.tipo)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${getPrioridadBadgeClass(noticia.prioridad)}`}>
                                            {getPrioridadLabel(noticia.prioridad)}
                                        </span>
                                    </td>
                                    <td>{new Date(noticia.fecha_inicio).toLocaleDateString()}</td>
                                    <td>
                                        {noticia.fecha_fin 
                                            ? new Date(noticia.fecha_fin).toLocaleDateString() 
                                            : '-'}
                                    </td>
                                    <td>
                                        <span className={`badge ${noticia.activo ? 'badge-success' : 'badge-secondary'}`}>
                                            {noticia.activo ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className={`btn btn-sm ${noticia.activo ? 'btn-warning' : 'btn-success'} mr-2`}
                                            onClick={() => handleToggleEstado(noticia.id, noticia.activo)}
                                            title={noticia.activo ? 'Desactivar' : 'Activar'}
                                        >
                                            <i className={`fas ${noticia.activo ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                        <Link
                                            to={`/superadmin/noticias/editar/${noticia.id}`}
                                            className="btn btn-sm btn-dark mr-2"
                                        >
                                            <i className="fas fa-edit"></i> Editar
                                        </Link>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDeleteClick(noticia)}
                                        >
                                            <i className="fas fa-trash"></i> Eliminar
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center">
                                    No se encontraron noticias
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
                <nav>
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Anterior
                            </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                            <li
                                key={index + 1}
                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                            >
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Siguiente
                            </button>
                        </li>
                    </ul>
                </nav>
            )}

            {/* Modal de Confirmación */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Está seguro que desea eliminar la noticia <strong>{noticiaToDelete?.titulo}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        Eliminar
                    </Button>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default Noticias;
